/**
 * SchoolTeam — Team Members, Roles & Permissions Management
 */
import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { getToken } from '@/lib/authToken';
import {
  Users, Shield, Plus, Edit2, Trash2, Loader2, Search,
  UserPlus, Building2, CheckCircle2, XCircle, Eye, KeyRound
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const api = async (path, opts = {}) => {
  const res = await fetch(`${API_URL}/api/school${path}`, {
    ...opts,
    headers: { 'Authorization': `Bearer ${getToken()}`, 'Content-Type': 'application/json', ...opts.headers },
  });
  if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || 'Request failed'); }
  return res.json();
};

const StatCard = ({ label, value, icon: Icon, color }) => (
  <Card><CardContent className="p-4"><div className="flex items-center gap-3">
    <div className={`w-10 h-10 rounded-lg bg-${color}-100 flex items-center justify-center`}><Icon className={`h-5 w-5 text-${color}-600`} /></div>
    <div><p className="text-xl font-bold text-slate-900">{value}</p><p className="text-xs text-slate-500">{label}</p></div>
  </div></CardContent></Card>
);

const DEPARTMENTS = ['Administration', 'Academic', 'Finance', 'IT', 'Library', 'Security', 'Transport', 'Maintenance', 'Other'];
const EMPLOYMENT_TYPES = [
  { value: 'full_time', label: 'Full Time' },
  { value: 'part_time', label: 'Part Time' },
  { value: 'contract', label: 'Contract' },
  { value: 'volunteer', label: 'Volunteer' },
];

export const SchoolTeam = ({ tenantId }) => {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const { data: dashboard } = useQuery({ queryKey: ['team-dashboard', tenantId], queryFn: () => api(`/tenants/${tenantId}/team/dashboard`) });
  const { data: members = [] } = useQuery({ queryKey: ['team-members', tenantId], queryFn: () => api(`/tenants/${tenantId}/team/members`) });
  const { data: roles = [] } = useQuery({ queryKey: ['team-roles', tenantId], queryFn: () => api(`/tenants/${tenantId}/team/roles`) });
  const { data: permissions = [] } = useQuery({ queryKey: ['team-permissions', tenantId], queryFn: () => api(`/tenants/${tenantId}/team/permissions`) });

  // Member CRUD
  const [showMemberForm, setShowMemberForm] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const emptyMemberForm = { name: '', email: '', phone: '', role_id: '', department: '', position: '', employment_type: 'full_time', date_joined: '', photo_url: '', is_active: true, notes: '' };
  const [mForm, setMForm] = useState(emptyMemberForm);

  const saveMember = useMutation({
    mutationFn: d => editingMember
      ? api(`/tenants/${tenantId}/team/members/${editingMember.id}`, { method: 'PUT', body: JSON.stringify(d) })
      : api(`/tenants/${tenantId}/team/members`, { method: 'POST', body: JSON.stringify(d) }),
    onSuccess: (data) => {
      // Surface the auto-generated temp password if present so the admin can share it with the new member
      if (data?.temp_password) {
        toast.success(`Team member saved. Temp password: ${data.temp_password}`, { duration: 15000 });
      } else {
        toast.success('Team member saved');
      }
      qc.invalidateQueries({ queryKey: ['team-members'] });
      qc.invalidateQueries({ queryKey: ['team-dashboard'] });
      setShowMemberForm(false);
    },
    onError: e => toast.error(e.message),
  });
  const deleteMember = useMutation({
    mutationFn: id => api(`/tenants/${tenantId}/team/members/${id}`, { method: 'DELETE' }),
    onSuccess: () => { toast.success('Removed'); qc.invalidateQueries({ queryKey: ['team-members'] }); qc.invalidateQueries({ queryKey: ['team-dashboard'] }); },
  });

  const resendPassword = useMutation({
    mutationFn: id => api(`/tenants/${tenantId}/team/members/${id}/resend-password`, { method: 'POST' }),
    onSuccess: (data) => {
      if (data?.email_sent) {
        toast.success(data.message || 'Password reset and emailed to the team member.');
      } else {
        toast.warning(
          (data?.message || 'Password reset.') + (data?.temp_password ? ` Temp: ${data.temp_password}` : ''),
          { duration: 10000 }
        );
      }
    },
    onError: e => toast.error(e.message),
  });

  // Role CRUD
  const [showRoleForm, setShowRoleForm] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [rForm, setRForm] = useState({ name: '', description: '', permissions: [] });
  const [showRoleDetail, setShowRoleDetail] = useState(null);

  const saveRole = useMutation({
    mutationFn: d => editingRole
      ? api(`/tenants/${tenantId}/team/roles/${editingRole.id}`, { method: 'PUT', body: JSON.stringify(d) })
      : api(`/tenants/${tenantId}/team/roles`, { method: 'POST', body: JSON.stringify(d) }),
    onSuccess: () => { toast.success('Role saved'); qc.invalidateQueries({ queryKey: ['team-roles'] }); setShowRoleForm(false); },
    onError: e => toast.error(e.message),
  });
  const deleteRole = useMutation({
    mutationFn: id => api(`/tenants/${tenantId}/team/roles/${id}`, { method: 'DELETE' }),
    onSuccess: () => { toast.success('Deleted'); qc.invalidateQueries({ queryKey: ['team-roles'] }); },
    onError: e => toast.error(e.message),
  });

  const togglePermission = (perm) => {
    setRForm(prev => ({
      ...prev,
      permissions: prev.permissions.includes(perm)
        ? prev.permissions.filter(p => p !== perm)
        : [...prev.permissions, perm],
    }));
  };

  const permissionGroups = permissions.reduce((acc, p) => {
    if (!acc[p.group]) acc[p.group] = [];
    acc[p.group].push(p);
    return acc;
  }, {});

  const filtered = members.filter(m =>
    !search || m.name?.toLowerCase().includes(search.toLowerCase()) ||
    m.email?.toLowerCase().includes(search.toLowerCase()) ||
    m.position?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-5" data-testid="school-team">
      <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2">
        <Users className="h-5 w-5 text-indigo-600" /> Team & Permissions
      </h2>

      {dashboard && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="team-dashboard">
          <StatCard label="Total Members" value={dashboard.total_members} icon={Users} color="indigo" />
          <StatCard label="Active" value={dashboard.active_members} icon={CheckCircle2} color="green" />
          <StatCard label="Inactive" value={dashboard.inactive_members} icon={XCircle} color="red" />
          <StatCard label="Roles" value={dashboard.total_roles} icon={Shield} color="purple" />
        </div>
      )}

      <Tabs defaultValue="members">
        <TabsList>
          <TabsTrigger value="members" data-testid="team-members-tab"><Users className="h-4 w-4 mr-1" /> Members ({members.length})</TabsTrigger>
          <TabsTrigger value="roles" data-testid="team-roles-tab"><Shield className="h-4 w-4 mr-1" /> Roles ({roles.length})</TabsTrigger>
        </TabsList>

        {/* MEMBERS TAB */}
        <TabsContent value="members" className="mt-4 space-y-3">
          <div className="flex items-center justify-between gap-3">
            <div className="relative flex-1 max-w-xs">
              <Search className="h-4 w-4 absolute left-3 top-2.5 text-slate-400" />
              <Input placeholder="Search members..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" data-testid="team-search" />
            </div>
            <Button size="sm" onClick={() => { setEditingMember(null); setMForm(emptyMemberForm); setShowMemberForm(true); }} data-testid="add-member-btn">
              <UserPlus className="h-4 w-4 mr-1" /> Add Member
            </Button>
          </div>

          <Table>
            <TableHeader><TableRow>
              <TableHead>Name</TableHead><TableHead>Role</TableHead><TableHead>Department</TableHead>
              <TableHead>Position</TableHead><TableHead>HR Status</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {filtered.map(m => (
                <TableRow key={m.id} data-testid={`member-row-${m.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-medium text-sm shrink-0">
                        {m.photo_url ? <img src={m.photo_url} alt="" className="w-full h-full object-cover rounded-full" /> : m.name?.charAt(0)?.toUpperCase()}
                      </div>
                      <div>
                        <p className="font-medium text-sm">{m.name}</p>
                        <p className="text-xs text-slate-400">{m.email}</p>
                        {m.hr_employee_number && <p className="text-[10px] text-slate-400 font-mono">{m.hr_employee_number}</p>}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell><Badge variant="outline" className="text-xs">{m.role_name}</Badge></TableCell>
                  <TableCell>
                    <div className="text-sm text-slate-600">{m.department || '—'}</div>
                    {m.hr_department && m.hr_department !== m.department && <div className="text-[10px] text-indigo-500">HR: {m.hr_department}</div>}
                  </TableCell>
                  <TableCell>
                    <div className="text-sm text-slate-600">{m.position || '—'}</div>
                    {m.hr_designation && <div className="text-[10px] text-indigo-500">{m.hr_designation}</div>}
                  </TableCell>
                  <TableCell>
                    {m.staff_id ? (
                      <Badge className="bg-blue-50 text-blue-700 text-[10px]">{m.hr_employment_status || 'Linked'}</Badge>
                    ) : (
                      <span className="text-[10px] text-slate-400">Not linked</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge className={m.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                      {m.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => {
                        setEditingMember(m);
                        setMForm({ name: m.name, email: m.email, phone: m.phone || '', role_id: m.role_id || '', department: m.department || '', position: m.position || '', employment_type: m.employment_type || 'full_time', date_joined: m.date_joined || '', photo_url: m.photo_url || '', is_active: m.is_active !== false, notes: m.notes || '' });
                        setShowMemberForm(true);
                      }} data-testid={`edit-member-${m.id}`}><Edit2 className="h-3.5 w-3.5" /></Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        title="Resend / reset password"
                        disabled={resendPassword.isPending && resendPassword.variables === m.id}
                        onClick={() => {
                          if (!m.email) { toast.error('Add an email to this member first.'); return; }
                          if (window.confirm(`Generate a new temporary password for ${m.name || m.email}?\nThey will receive it by email.`)) {
                            resendPassword.mutate(m.id);
                          }
                        }}
                        data-testid={`reset-password-${m.id}`}
                      >
                        {resendPassword.isPending && resendPassword.variables === m.id
                          ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          : <KeyRound className="h-3.5 w-3.5 text-amber-500" />}
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => window.confirm('Remove this team member?') && deleteMember.mutate(m.id)}>
                        <Trash2 className="h-3.5 w-3.5 text-red-400" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-8">No team members yet. Click "Add Member" to get started.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </TabsContent>

        {/* ROLES TAB */}
        <TabsContent value="roles" className="mt-4 space-y-3">
          <div className="flex justify-end">
            <Button size="sm" onClick={() => { setEditingRole(null); setRForm({ name: '', description: '', permissions: [] }); setShowRoleForm(true); }} data-testid="add-role-btn">
              <Plus className="h-4 w-4 mr-1" /> Add Role
            </Button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {roles.map(r => (
              <Card key={r.id} data-testid={`role-card-${r.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-medium text-sm flex items-center gap-1.5">
                        <Shield className="h-4 w-4 text-indigo-500" /> {r.name}
                        {r.is_system && <Badge className="bg-slate-100 text-slate-500 text-[10px]">System</Badge>}
                      </h3>
                      <p className="text-xs text-slate-400 mt-0.5">{r.description}</p>
                    </div>
                    <Badge variant="outline" className="text-xs">{r.member_count} members</Badge>
                  </div>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {(r.permissions || []).slice(0, 4).map(p => (
                      <Badge key={p} className="bg-indigo-50 text-indigo-600 text-[10px]">{p}</Badge>
                    ))}
                    {(r.permissions || []).length > 4 && <Badge className="bg-slate-100 text-slate-500 text-[10px]">+{r.permissions.length - 4}</Badge>}
                    {r.permissions?.[0] === '*' && <Badge className="bg-amber-50 text-amber-700 text-[10px]">Full Access</Badge>}
                  </div>
                  <div className="flex gap-1 mt-3">
                    <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setShowRoleDetail(r)} data-testid={`view-role-${r.id}`}>
                      <Eye className="h-3 w-3 mr-1" /> View
                    </Button>
                    <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => {
                      setEditingRole(r);
                      setRForm({ name: r.name, description: r.description || '', permissions: r.permissions || [] });
                      setShowRoleForm(true);
                    }} data-testid={`edit-role-${r.id}`}><Edit2 className="h-3 w-3 mr-1" /> Edit</Button>
                    {!r.is_system && (
                      <Button variant="outline" size="sm" className="h-7 text-xs text-red-500" onClick={() => window.confirm('Delete this role?') && deleteRole.mutate(r.id)}>
                        <Trash2 className="h-3 w-3 mr-1" /> Delete
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Member Form Dialog */}
      <Dialog open={showMemberForm} onOpenChange={setShowMemberForm}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" aria-describedby="member-form-desc">
          <DialogHeader><DialogTitle>{editingMember ? 'Edit' : 'Add'} Team Member</DialogTitle><p id="member-form-desc" className="sr-only">Team member form</p></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Name *</Label><Input value={mForm.name} onChange={e => setMForm({ ...mForm, name: e.target.value })} data-testid="member-name" /></div>
              <div><Label>Email *</Label><Input type="email" value={mForm.email} onChange={e => setMForm({ ...mForm, email: e.target.value })} data-testid="member-email" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Phone</Label><Input value={mForm.phone} onChange={e => setMForm({ ...mForm, phone: e.target.value })} /></div>
              <div><Label>Role</Label>
                <Select value={mForm.role_id} onValueChange={v => setMForm({ ...mForm, role_id: v })}>
                  <SelectTrigger data-testid="member-role"><SelectValue placeholder="Select role..." /></SelectTrigger>
                  <SelectContent>{roles.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Department</Label>
                <Select value={mForm.department} onValueChange={v => setMForm({ ...mForm, department: v })}>
                  <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>{DEPARTMENTS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Position</Label><Input value={mForm.position} onChange={e => setMForm({ ...mForm, position: e.target.value })} placeholder="e.g. Head of Department" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Employment Type</Label>
                <Select value={mForm.employment_type} onValueChange={v => setMForm({ ...mForm, employment_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{EMPLOYMENT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Date Joined</Label><Input type="date" value={mForm.date_joined} onChange={e => setMForm({ ...mForm, date_joined: e.target.value })} /></div>
            </div>
            <div><Label>Notes</Label><Textarea value={mForm.notes} onChange={e => setMForm({ ...mForm, notes: e.target.value })} rows={2} placeholder="Additional notes..." /></div>
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={mForm.is_active} onChange={e => setMForm({ ...mForm, is_active: e.target.checked })} id="member-active" />
              <Label htmlFor="member-active">Active</Label>
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
            <Button onClick={() => saveMember.mutate(mForm)} disabled={!mForm.name || !mForm.email || saveMember.isPending} data-testid="save-member-btn">
              {saveMember.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Role Form Dialog */}
      <Dialog open={showRoleForm} onOpenChange={setShowRoleForm}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" aria-describedby="role-form-desc">
          <DialogHeader><DialogTitle>{editingRole ? 'Edit' : 'Create'} Role</DialogTitle><p id="role-form-desc" className="sr-only">Role form with permissions</p></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Role Name *</Label><Input value={rForm.name} onChange={e => setRForm({ ...rForm, name: e.target.value })} data-testid="role-name" /></div>
              <div><Label>Description</Label><Input value={rForm.description} onChange={e => setRForm({ ...rForm, description: e.target.value })} /></div>
            </div>
            <div>
              <Label className="text-sm font-medium">Permissions</Label>
              <div className="mt-2 space-y-3">
                <label className="flex items-center gap-2 text-sm font-medium text-amber-700 bg-amber-50 p-2 rounded">
                  <input type="checkbox" checked={rForm.permissions.includes('*')} onChange={() => {
                    setRForm(prev => ({ ...prev, permissions: prev.permissions.includes('*') ? [] : ['*'] }));
                  }} />
                  Full Access (all permissions)
                </label>
                {!rForm.permissions.includes('*') && Object.entries(permissionGroups).map(([group, perms]) => (
                  <div key={group} className="border rounded-lg p-3">
                    <p className="text-xs font-semibold text-slate-500 mb-2">{group}</p>
                    <div className="flex flex-wrap gap-2">
                      {perms.map(p => (
                        <label key={p.key} className="flex items-center gap-1.5 text-xs bg-slate-50 px-2 py-1 rounded cursor-pointer hover:bg-indigo-50">
                          <input type="checkbox" checked={rForm.permissions.includes(p.key)} onChange={() => togglePermission(p.key)} className="accent-indigo-600" />
                          {p.label}
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
            <Button onClick={() => saveRole.mutate(rForm)} disabled={!rForm.name || saveRole.isPending} data-testid="save-role-btn">
              {saveRole.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Save Role
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Role Detail Dialog */}
      <Dialog open={!!showRoleDetail} onOpenChange={() => setShowRoleDetail(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" aria-describedby="role-detail-desc">
          <DialogHeader><DialogTitle>{showRoleDetail?.name}</DialogTitle><p id="role-detail-desc" className="sr-only">Role details</p></DialogHeader>
          {showRoleDetail && (
            <div className="space-y-3">
              <p className="text-sm text-slate-500">{showRoleDetail.description}</p>
              {showRoleDetail.is_system && <Badge className="bg-slate-100 text-slate-600">System Role</Badge>}
              <div>
                <p className="text-xs font-semibold text-slate-500 mb-2">Permissions ({showRoleDetail.permissions?.length || 0})</p>
                {showRoleDetail.permissions?.[0] === '*' ? (
                  <Badge className="bg-amber-100 text-amber-800">Full Access — All Permissions</Badge>
                ) : (
                  <div className="flex flex-wrap gap-1">
                    {(showRoleDetail.permissions || []).map(p => (
                      <Badge key={p} variant="outline" className="text-xs">{p}</Badge>
                    ))}
                  </div>
                )}
              </div>
              <p className="text-xs text-slate-400">{showRoleDetail.member_count} member(s) assigned</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SchoolTeam;
